/**
 * SophieAgent — floating help guide.
 *
 * Renders a persistent circular Sophie avatar button on the bottom-right
 * (above the bottom-nav on mobile). Tapping it opens a chat sheet:
 *   • First-time (no localStorage flag): intro card + starter prompts + tour
 *     button. On dismiss, we flip `scooby:intro-seen=1` so it doesn't
 *     re-open unbidden.
 *   • Returning: standard message list + composer. History persists per user
 *     via POST /api/scooby/chat.
 *
 * Persona: Scooby, a warm dog-mascot help guide. Different from the AI
 * Stylist (styling advice) — this one is scoped to app how-tos and
 * troubleshooting.
 *
 * Wired into <Layout /> so every authed screen carries it. Hidden on
 * /login, /signup, /onboarding, /delete-account, /grievance (those are
 * either pre-auth or need full-screen focus).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  X, Send, Loader2, Sparkles, Trash2, HelpCircle, ArrowRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_INTRO_SEEN = "scooby:intro-seen";
const STORAGE_OPEN = "scooby:hint-shown";

const HIDDEN_ROUTES = new Set([
  "/login", "/signup", "/onboarding", "/delete-account", "/grievance",
]);

const DEFAULT_PROMPTS = [
  "Give me a quick tour of Stylebay",
  "How does the AI Try-On work?",
  "How do I upload my wardrobe?",
  "How do I book a stylist?",
  "How does the Beauty Scan work?",
  "What do I get with Premium?",
];

export default function SophieAgent() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [introSeen, setIntroSeen] = useState(() => {
    try { return localStorage.getItem(STORAGE_INTRO_SEEN) === "1"; } catch { return true; }
  });
  const [messages, setMessages] = useState([]); // {role, content}
  const [prompts, setPrompts] = useState(DEFAULT_PROMPTS);
  const [greeting, setGreeting] = useState(
    "Hi, I'm Sophie — your AI fashion bestie. Let me take you through Stylebay!",
  );
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollerRef = useRef(null);

  const hidden = HIDDEN_ROUTES.has(location.pathname) || !user;

  // Fetch intro + prompts once on mount (public endpoint, cached in state)
  useEffect(() => {
    if (hidden) return;
    api.get("/scooby/intro").then(({ data }) => {
      if (data.greeting) setGreeting(data.greeting);
      if (Array.isArray(data.prompts) && data.prompts.length) setPrompts(data.prompts);
    }).catch(() => {});
  }, [hidden]);

  // On first sign-in show a subtle hint bubble; auto-open only if user
  // hasn't been onboarded to Scooby yet. Fires once per browser.
  useEffect(() => {
    if (hidden || introSeen) return;
    try {
      if (localStorage.getItem(STORAGE_OPEN) === "1") return;
      const t = setTimeout(() => {
        setOpen(true);
        localStorage.setItem(STORAGE_OPEN, "1");
      }, 1500);
      return () => clearTimeout(t);
    } catch { /* private-browsing localStorage disabled — no-op */ }
  }, [hidden, introSeen]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = useCallback(async (text) => {
    const msg = (text || "").trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/scooby/chat", { message: msg, session_id: sessionId });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const err = typeof detail === "string" ? detail : (detail?.message || e?.message || "Sophie got distracted — try again.");
      setMessages((m) => [...m, { role: "assistant", content: err, error: true }]);
    } finally {
      setSending(false);
    }
  }, [sending, sessionId]);

  const dismissIntro = () => {
    try { localStorage.setItem(STORAGE_INTRO_SEEN, "1"); } catch { /* noop */ }
    setIntroSeen(true);
  };

  const clearHistory = async () => {
    try {
      await api.delete("/scooby/history");
      setMessages([]);
      setSessionId(null);
      toast.success("Sophie forgot our chat.");
    } catch { toast.error("Could not clear history."); }
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          data-testid="scooby-open-btn"
          onClick={() => { setOpen(true); if (!introSeen) dismissIntro(); }}
          className="fixed z-[60] bottom-[calc(88px+env(safe-area-inset-bottom))] right-4 md:bottom-[calc(88px+env(safe-area-inset-bottom))] h-16 w-16 rounded-full border border-border shadow-xl bg-gradient-to-br from-pink-100 via-rose-50 to-pink-50 overflow-hidden hover:scale-105 active:scale-95 transition-transform"
          aria-label="Open Sophie help"
          style={{ padding: 0 }}
        >
          <img
            src="/sophie.png"
            alt="Sophie — your AI fashion bestie"
            className="w-full h-full object-contain"
            draggable="false"
          />
          {!introSeen && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-accent rounded-full animate-pulse" />
          )}
        </button>
      )}

      {/* Chat sheet */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="scooby-panel"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.22 }}
            className="fixed z-[60] bottom-[calc(88px+env(safe-area-inset-bottom))] right-4 md:bottom-[calc(88px+env(safe-area-inset-bottom))] md:right-6 w-[calc(100vw-2rem)] max-w-[380px] h-[70vh] max-h-[600px] bg-background border border-border shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-3 border-b border-border bg-muted/30">
              <div className="h-10 w-10 rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-pink-100 via-rose-50 to-pink-50 border border-border">
                <img src="/sophie.png" alt="" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg leading-none">Sophie</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">Your AI fashion bestie</p>
              </div>
              {messages.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="text-muted-foreground hover:text-destructive"
                  data-testid="scooby-clear-btn"
                  aria-label="Clear chat history"
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="scooby-close-btn"
                aria-label="Close Sophie"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {!introSeen && messages.length === 0 ? (
                <IntroCard
                  greeting={greeting}
                  onTour={() => { dismissIntro(); send("Give me a quick tour of Stylebay"); }}
                  onSkip={() => { dismissIntro(); }}
                />
              ) : messages.length === 0 ? (
                <EmptyStateCard greeting={greeting} />
              ) : (
                messages.map((m, i) => <Bubble key={i} m={m} />)
              )}
              {sending && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pl-1" data-testid="scooby-thinking">
                  <Loader2 size={13} className="animate-spin" /> Sophie is styling your answer…
                </div>
              )}
            </div>

            {/* Prompt chips */}
            {messages.length === 0 && (
              <div className="px-3 pb-2 space-y-1.5" data-testid="scooby-prompts">
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground px-1">Try asking</p>
                <div className="flex flex-wrap gap-1.5">
                  {prompts.slice(0, 4).map((p) => (
                    <button
                      key={p}
                      onClick={() => send(p)}
                      className="text-[11px] px-2.5 py-1.5 border border-border hover:border-foreground text-left leading-tight"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Composer */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(input); }}
              className="p-3 border-t border-border flex gap-2 items-center bg-background"
            >
              <Input
                data-testid="scooby-input"
                placeholder="Ask Sophie anything…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={sending}
                maxLength={500}
                className="rounded-none h-10 text-sm"
              />
              <Button
                type="submit"
                data-testid="scooby-send-btn"
                disabled={!input.trim() || sending}
                className="rounded-none h-10 w-10 p-0 shrink-0"
                aria-label="Send"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Bubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`scooby-msg-${m.role}`}>
      <div
        className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
          isUser
            ? "bg-accent text-accent-foreground"
            : m.error
              ? "bg-destructive/10 text-destructive border border-destructive/30"
              : "bg-muted"
        }`}
      >
        {renderScoobyText(m.content)}
      </div>
    </div>
  );
}

/** Convert *route mentions* + **bold** into simple JSX. Bold and italics only.
 *  Route mentions are already italicized by the model; we just make sure
 *  they don't get raw asterisks in the UI. */
function renderScoobyText(text) {
  if (!text) return null;
  const parts = [];
  const rx = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let last = 0;
  let m;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const chunk = m[0];
    if (chunk.startsWith("**")) {
      parts.push(<b key={m.index}>{chunk.slice(2, -2)}</b>);
    } else {
      parts.push(<i key={m.index} className="opacity-90">{chunk.slice(1, -1)}</i>);
    }
    last = m.index + chunk.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function IntroCard({ greeting, onTour, onSkip }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-4"
      data-testid="scooby-intro-card"
    >
      <div className="mx-auto w-24 h-24 rounded-full overflow-hidden border border-border bg-gradient-to-br from-pink-100 via-rose-50 to-pink-50">
        <img src="/sophie.png" alt="Sophie" className="w-full h-full object-contain" />
      </div>
      <p className="eyebrow mt-4 flex items-center justify-center gap-1.5">
        <Sparkles size={11} /> Meet Sophie
      </p>
      <p className="font-serif text-2xl leading-tight mt-2 px-2">{greeting}</p>
      <p className="text-xs text-muted-foreground mt-3 px-4 leading-relaxed">
        I&apos;ll show you the best of Stylebay in under a minute — try-on, wardrobe, stylists, beauty scan, and more.
      </p>
      <div className="flex flex-col gap-2 mt-6 px-2">
        <Button
          data-testid="scooby-start-tour-btn"
          onClick={onTour}
          className="rounded-none h-11 uppercase tracking-[0.2em] text-[10px] gap-2"
        >
          Take the tour <ArrowRight size={12} />
        </Button>
        <button
          data-testid="scooby-skip-tour-btn"
          onClick={onSkip}
          className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground py-2 hover:text-foreground"
        >
          I&apos;ll explore on my own
        </button>
      </div>
    </motion.div>
  );
}

function EmptyStateCard({ greeting }) {
  return (
    <div className="text-center py-8" data-testid="scooby-empty">
      <HelpCircle size={28} className="mx-auto text-muted-foreground" strokeWidth={1.2} />
      <p className="text-sm mt-3 max-w-xs mx-auto leading-relaxed">{greeting}</p>
      <p className="text-[11px] text-muted-foreground mt-3">Pick a prompt below or ask me anything.</p>
    </div>
  );
}
